import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import ModalRoute from "../../components/common/ModalRoute";
import Account from "./Account";
import Preferences from "./Preferences";
import Notifications from "./Notifications";
import Subscription from "./Subscription";
import SubscriptionSelect from "./SubscriptionSelect";
import SubscriptionCancel from "./SubscriptionCancel";
import AccountApi from "./Api";

const SettingsIndex = (authProps) => {
  const { currentUser } = authProps;
  const location = useLocation();
  const tabs = [
    {
      id: "projects",
      label: "Projects",
      path: "projects",
      to: "/account/projects/",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "subscription",
      label: "Subscription",
      path: "subscription",
      to: "/account/subscription/",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
    },
    {
      id: "notifications",
      label: "Notifications",
      path: "notifications",
      to: "/account/notifications/",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
    },
    {
      id: "api",
      label: "API",
      path: "api",
      to: "/account/api/",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
          />
        </svg>
      ),
    },
    {
      id: "preferences",
      label: "Preferences",
      path: "preferences",
      to: "/account/preferences/",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
  ];

  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes("/subscription/")) return "subscription";
    if (path.includes("/api/")) return "api";
    for (const tab of tabs) {
      if (path.includes(`/${tab.path}/`) || path.endsWith(`/${tab.path}`)) {
        return tab.id;
      }
    }
    return "projects";
  };

  const activeTab = getActiveTabFromPath();

  const navConfig = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    to: tab.to,
    active: activeTab === tab.id,
  }));

  return (
    <DashboardLayout
      title="Account"
      currentUser={currentUser}
      navConfig={navConfig}
    >
      <Routes>
        <Route path="projects" element={<Account {...authProps} />} />
        <Route
          path="subscription/*"
          element={<Subscription {...authProps} />}
        />
        <Route path="api/*" element={<AccountApi {...authProps} />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="preferences" element={<Preferences />} />
        <Route path="" element={<Navigate to="projects/" replace />} />
        <Route path="*" element={<Navigate to="projects/" replace />} />
      </Routes>
      <ModalRoute
        path="/account/subscription/select"
        parentPath="/account/subscription/"
        element={<SubscriptionSelect {...authProps} />}
        maxWidth="max-w-5xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/account/subscription/cancel"
        parentPath="/account/subscription/"
        element={<SubscriptionCancel {...authProps} />}
        maxWidth="max-w-lg"
        heightClass="h-auto max-h-[90vh]"
        noPadding
      />
    </DashboardLayout>
  );
};

export default SettingsIndex;
